import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { __t, applyTranslations } from 'utils/language';

import './attachments-with-view-delete.scss';

applyTranslations(require('./attachments-with-view-delete.i18n.json'));

import type { MediaPreviewAttachment } from 'types';

type OwnProps = {
  attachments: Array<MediaPreviewAttachment>,
  uniqueId: number | string,
  isViewMode?: boolean,
  hideHeader?: boolean,
  onView?: () => void,
  onDelete?: (id: string | number) => void
}

type DispatchProps ={
  dispatchShowConfirmationModal: (
    params: {
      title: string,
      message: string,
      confirmButtonLabel?: string,
      onConfirm: () => void,
      onCancel?: () => void
    }
  ) => Promise<void>,
  dispatchHideConfirmationModal: () => Promise<void>
}

type Props = OwnProps & DispatchProps;

const AttachmentsWithViewDelete = ({
  attachments,
  isViewMode,
  uniqueId,
  hideHeader,
  onView,
  onDelete,
  dispatchShowConfirmationModal,
  dispatchHideConfirmationModal }: Props) => {
  const handleDeleteAttachement = (attachment: MediaPreviewAttachment) => {
    dispatchShowConfirmationModal({
      title: `${attachment.fileName || ''}`,
      message: __t('attachments_with_view_delete.confirm_delete'),
      confirmButtonLabel: __t('attachments_with_view_delete.confirm_delete_btn'),
      onConfirm: () => {
        dispatchHideConfirmationModal();

        if (onDelete) {
          onDelete(attachment.id);
        }
      },
      onCancel: () => {
        dispatchHideConfirmationModal();
      }
    });
  };

  const handleOpenFile = (url: string) => {
    if (onView) {
      onView();

      return;
    }
    const win = window.open(url, '_blank');

    if (win) {
      win.focus();
    }
  };

  const renderFilename = (attachment: MediaPreviewAttachment): React.ReactNode => {
    const noUnderLineClass = attachment.id ? '' : '-no-underline-onhover';

    return (
      <div
        className={`attachments-with-view-delete_filename${noUnderLineClass}`}
        onClick={() => {
          if (attachment.id) {
            handleOpenFile(attachment.url);
          }
        }}
      >
        {attachment.fileName}
      </div>
    );
  };

  const renderRow = (attachment: MediaPreviewAttachment, index) => (
    <div
      className="attachments-with-view-delete__row"
      key={attachment.id || `attachments-with-view-delete-${index}-${uniqueId}`}
    >
      {renderFilename(attachment)}
      {
        attachment.id && (
          <div className="attachments-with-view-delete__view-file">
            <Tooltip
              message={__t('attachments_with_view_delete.header.view')}
              width="micro"
              trigger="hover"
            >
              <SVGDynamicIcon
                name="eye-custom-bg"
                width={16}
                height={16}
                clickable
                customColor="grey"
                onClick={() => handleOpenFile(attachment.url)}
              />
            </Tooltip>
          </div>
        )
      }
      {
        onDelete && (
          <div className="attachments-with-view-delete__delete-file">
            <Tooltip
              message={__t('attachments_with_view_delete.header.delete')}
              width="micro"
              trigger="hover"
            >
              <SVGDynamicIcon
                name="trash-custom-fg"
                width={16}
                height={16}
                clickable
                customColor="grey"
                onClick={() => handleDeleteAttachement(attachment)}
              />
            </Tooltip>
          </div>
        )
      }
    </div>
  );

  const renderHeader = () => (
    <div className="attachments-with-view-delete__header">
      <div className="attachments-with-view-delete__name">
        {__t('attachments_with_view_delete.header.attachment')}
      </div>
    </div>
  );

  const containerClass = classnames('attachments-with-view-delete__main', {
    'attachments-with-view-delete__main--view-mode': isViewMode
  });

  return (
    <div className={containerClass}>
      {!hideHeader && renderHeader()}
      {
        attachments.map(renderRow)
      }
    </div>
  );
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchShowConfirmationModal: (props) => dispatch(showConfirmModal(props)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal())
});

export default connect(null, mapDispatchToProps)(AttachmentsWithViewDelete);
